import {
  CheckIcon,
  DashboardIcon,
  LineIcon,
  MakerIcon,
  MapIcon
} from "@/assets/icons";
import { SPATIAL_AGGREGATION_NAME } from "@/constants/dataset";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import { ExtendedSpatialAggregationType } from "@/types/common";
import { EOutputAnalysisType } from "@/types/spatial";

interface AggregationConfig {
  icon?: JSX.Element;
  bgColor: string;
  label?: string;
}

/** ADM Area Mapping is not a spatial aggregation — no dataset carries it as one
 * — but it is badged in the same rows, so it lives here rather than widening
 * SpatialAggregationType, which types the dataset field itself. */
export type AggregationBadgeValue =
  | Exclude<ExtendedSpatialAggregationType, EOutputAnalysisType>
  | "adm_area_mapping";

interface AggregationBadgeProps {
  value?: AggregationBadgeValue | null | "";
}

const aggregationStyles: Record<AggregationBadgeValue, AggregationConfig> = {
  poi: {
    icon: <MakerIcon width={16} height={16} />,
    bgColor:
      "fill-purple-700 bg-purple-50 border border-purple-200 text-purple-700"
  },
  polygon: {
    icon: <DashboardIcon width={16} height={16} />,
    bgColor:
      "fill-orange-700 bg-orange-50 border border-orange-200 text-orange-700"
  },
  adm_area: {
    icon: <MapIcon width={16} height={16} />,
    bgColor: "fill-blue-700 bg-blue-50 border border-blue-200 text-blue-700"
  },
  H3: {
    icon: <DashboardIcon width={16} height={16} />,
    bgColor:
      "fill-orange-700 bg-orange-50 border border-orange-200 text-orange-700"
  },
  geohash: {
    icon: <DashboardIcon width={16} height={16} />,
    bgColor:
      "fill-orange-700 bg-orange-50 border border-orange-200 text-orange-700"
  },
  line: {
    icon: <LineIcon width={16} height={16} className="fill-[#AF6306]" />,
    bgColor: "bg-orange-50 border border-orange-200 text-[#AF6306]"
  },
  adm_area_mapping: {
    icon: <CheckIcon width={16} height={16} />,
    bgColor: "fill-blue-700 bg-blue-50 border border-blue-200 text-blue-700",
    label: DATA_SETTINGS_MESSAGE.ADM_AREA_BADGE_LABEL
  },
  // DEFAULT STATE
  default: {
    bgColor: "bg-neutral-50 border border-neutral-100 text-neutral-300",
    label: "None"
  }
};

export default function AggregationBadge({
  value
}: Readonly<AggregationBadgeProps>) {
  const isValid =
    value !== null &&
    value !== undefined &&
    value !== "" &&
    value !== "default" &&
    !!aggregationStyles[value];

  const config = isValid
    ? aggregationStyles[value]
    : aggregationStyles["default"];

  const label =
    config.label ??
    (isValid
      ? SPATIAL_AGGREGATION_NAME[value as keyof typeof SPATIAL_AGGREGATION_NAME]
      : undefined);

  return (
    <span
      className={`inline-flex items-center gap-1 py-1 px-2 rounded-lg font-semibold text-xs ${config.bgColor}`}
    >
      <span>{config.icon}</span>
      <span>{label}</span>
    </span>
  );
}
